import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { pendingIdentityOf, pendingMfaState } from '@/features/auth/mfa/session';

import { platformLogin, platformLogout, platformMe } from './api';
import { PLATFORM_SESSION_KEY } from './sessionKey';
import type { PlatformOperatorProfile } from './types';

export { PLATFORM_SESSION_KEY };

/**
 * The Fidelen operator's own session: the platform guard, its own query keys.
 *
 * Never shared with the tenant or supplier session hooks. Signing in here
 * grants nothing in the buyer workspace or the supplier portal, and the
 * server refuses the other way round too; the separate hook keeps it that way
 * in the client as well.
 */
export function usePlatformSession() {
  const query = useQuery({
    queryKey: PLATFORM_SESSION_KEY,
    queryFn: platformMe,
    retry: false,
    staleTime: 60_000,
  });
  const payload = query.data?.data ?? null;
  /*
   * Every operator must complete MFA. Until the session has, it is pending: no
   * operator profile, no permissions, no console — only the MFA step.
   */
  const mfaPending = pendingMfaState(payload);
  const operator =
    payload !== null && mfaPending === null ? (payload as PlatformOperatorProfile) : null;

  return {
    operator,
    isAuthenticated: operator !== null,
    isLoading: query.isPending,
    mfaPending,
    pendingIdentity: mfaPending === null ? null : pendingIdentityOf(payload),
    can: (permission: string) => operator?.permissions.includes(permission) ?? false,
  };
}

export function usePlatformLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: platformLogin,
    // The password is this mutation's variables: gone as soon as the screen is.
    gcTime: 0,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform'] }),
  });
}

export function usePlatformLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: platformLogout,
    // Everything the operator read goes with the session.
    onSettled: () => queryClient.removeQueries({ queryKey: ['platform'] }),
  });
}
